/**
 * OB Measurement Overlay Panel
 * Collapsible floating panel for OB measurements alongside OHIF viewer
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Typography,
  Tabs,
  Tab,
  Tooltip,
  Fab,
  Slide,
  Button,
  Divider,
  Badge,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PregnantWomanIcon from '@mui/icons-material/PregnantWoman';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import TimelineIcon from '@mui/icons-material/Timeline';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MinimizeIcon from '@mui/icons-material/Remove';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

import OBMeasurementPanel from './OBMeasurementPanel';
import DopplerAssessment from './DopplerAssessment';
import FetalGrowthChart from './FetalGrowthChart';
import { formatGA, formatWeight } from '../../utils/obCalculations';

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`ob-tabpanel-${index}`}
    aria-labelledby={`ob-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

const OBMeasurementOverlay = ({
  onCopyToReport,
  lmpDate,
  examDate,
  patientName,
  gestationalAgeByLMP,
  initialExpanded = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isExpanded, setIsExpanded] = useState(initialExpanded);
  const [isMinimized, setIsMinimized] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Store all measurement data
  const [biometryData, setBiometryData] = useState(null);
  const [dopplerData, setDopplerData] = useState(null);

  // Track if measurements have been entered
  const hasBiometry = biometryData?.results?.efw || biometryData?.results?.ga;
  const hasDoppler = dopplerData?.umbilicalArtery?.pi || dopplerData?.mca?.pi;

  const handleBiometryChange = useCallback((data) => {
    setBiometryData(data);
  }, []);

  const handleDopplerChange = useCallback((data) => {
    setDopplerData(data);
  }, []);

  const generateReportText = () => {
    const lines = [];

    // Biometry section
    if (biometryData?.results) {
      const { efw, ga, percentile, edd, hcAcRatio, flAcRatio, gaIndividual } = biometryData.results;
      const { bpd, hc, ac, fl, crl } = biometryData.measurements || {};
      const { afi, sdp } = biometryData.amnioticFluid || {};

      lines.push('FETAL BIOMETRY:');

      // Measurements
      const measurementParts = [];
      if (bpd) measurementParts.push(`BPD ${bpd} mm`);
      if (hc) measurementParts.push(`HC ${hc} mm`);
      if (ac) measurementParts.push(`AC ${ac} mm`);
      if (fl) measurementParts.push(`FL ${fl} mm`);
      if (crl) measurementParts.push(`CRL ${crl} mm`);
      if (measurementParts.length > 0) {
        lines.push(measurementParts.join(', '));
      }

      // Calculations
      if (efw) lines.push(`Estimated Fetal Weight: ${formatWeight(efw)}`);
      if (ga) lines.push(`Gestational Age by Ultrasound: ${formatGA(ga)}`);
      if (percentile) {
        const percentileText = percentile.percentile < 10
          ? `${percentile.percentile}th percentile (below normal)`
          : percentile.percentile > 90
          ? `${percentile.percentile}th percentile (above normal)`
          : `${percentile.percentile}th percentile (normal)`;
        lines.push(`Growth Percentile: ${percentileText}`);
      }
      if (edd) {
        lines.push(`Estimated Due Date: ${edd.toLocaleDateString('en-US', {
          year: 'numeric', month: 'long', day: 'numeric'
        })}`);
      }

      // Ratios
      if (hcAcRatio || flAcRatio) {
        const ratioParts = [];
        if (hcAcRatio) ratioParts.push(`HC/AC ratio ${hcAcRatio}`);
        if (flAcRatio) ratioParts.push(`FL/AC ratio ${flAcRatio}`);
        lines.push(ratioParts.join(', '));
      }

      // Amniotic fluid
      if (afi || sdp) {
        const fluidParts = [];
        if (afi) fluidParts.push(`AFI ${afi} cm`);
        if (sdp) fluidParts.push(`SDP ${sdp} cm`);
        lines.push(`Amniotic Fluid: ${fluidParts.join(', ')}`);
      }

      lines.push('');
    }

    // Doppler section
    if (dopplerData) {
      const { umbilicalArtery, mca, uterineArtery, cpr } = dopplerData;

      if (umbilicalArtery?.pi || mca?.pi || uterineArtery?.leftPI) {
        lines.push('DOPPLER ASSESSMENT:');

        if (umbilicalArtery?.pi) {
          let uaText = `Umbilical Artery: PI ${umbilicalArtery.pi.toFixed(2)}`;
          if (umbilicalArtery.ri) uaText += `, RI ${umbilicalArtery.ri.toFixed(2)}`;
          if (umbilicalArtery.sdRatio) uaText += `, S/D ${umbilicalArtery.sdRatio.toFixed(1)}`;
          if (umbilicalArtery.interpretation) uaText += ` - ${umbilicalArtery.interpretation.status}`;
          lines.push(uaText);
        }

        if (mca?.pi) {
          let mcaText = `Middle Cerebral Artery: PI ${mca.pi.toFixed(2)}`;
          if (mca.psv) mcaText += `, PSV ${mca.psv.toFixed(1)} cm/s`;
          if (mca.mom) mcaText += ` (${mca.mom.toFixed(2)} MoM)`;
          lines.push(mcaText);
        }

        if (cpr?.value) {
          lines.push(`Cerebroplacental Ratio: ${cpr.value.toFixed(2)} - ${cpr.interpretation?.status || 'calculated'}`);
        }

        if (uterineArtery?.leftPI || uterineArtery?.rightPI) {
          let utText = 'Uterine Arteries: ';
          if (uterineArtery.leftPI) utText += `Left PI ${uterineArtery.leftPI.toFixed(2)}`;
          if (uterineArtery.rightPI) utText += `, Right PI ${uterineArtery.rightPI.toFixed(2)}`;
          if (uterineArtery.meanPI) utText += ` (Mean ${uterineArtery.meanPI.toFixed(2)})`;
          lines.push(utText);
        }

        lines.push('');
      }
    }

    return lines.join('\n');
  };

  const handleCopyToReport = () => {
    const reportText = generateReportText();

    if (onCopyToReport) {
      onCopyToReport(reportText, { biometryData, dopplerData });
      setSnackbar({
        open: true,
        message: 'Measurements added to report findings',
        severity: 'success'
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(reportText).then(() => {
        setSnackbar({
          open: true,
          message: 'Copied to clipboard! Paste into findings field.',
          severity: 'info'
        });
      });
    }
  };

  const handleCopyToClipboard = () => {
    const reportText = generateReportText();
    navigator.clipboard.writeText(reportText).then(() => {
      setSnackbar({
        open: true,
        message: 'Copied to clipboard!',
        severity: 'success'
      });
    });
  };

  // Collapsed FAB button
  if (!isExpanded) {
    return (
      <Box
        sx={{
          position: 'fixed',
          right: { xs: 16, md: 20 },
          bottom: { xs: 80, md: 'auto' },
          top: { xs: 'auto', md: '50%' },
          transform: { xs: 'none', md: 'translateY(-50%)' },
          zIndex: 1200,
        }}
      >
        <Tooltip title="OB Measurements" placement="left">
          <Badge
            badgeContent={hasBiometry || hasDoppler ? '✓' : null}
            color="success"
            overlap="circular"
          >
            <Fab
              color="primary"
              onClick={() => setIsExpanded(true)}
              sx={{
                width: { xs: 56, md: 64 },
                height: { xs: 56, md: 64 },
                boxShadow: 4,
                '&:hover': {
                  transform: 'scale(1.1)',
                },
                transition: 'transform 0.2s',
              }}
            >
              <PregnantWomanIcon sx={{ fontSize: { xs: 28, md: 32 } }} />
            </Fab>
          </Badge>
        </Tooltip>
      </Box>
    );
  }

  return (
    <>
      <Slide direction={isMobile ? "up" : "left"} in={isExpanded} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            // Mobile: bottom sheet
            ...(isMobile ? {
              left: 0,
              right: 0,
              bottom: 0,
              top: 'auto',
              width: '100%',
              maxHeight: '70vh',
              borderRadius: '16px 16px 0 0',
            } : {
              // Desktop: side panel
              right: 16,
              top: 80,
              bottom: 100,
              width: isMinimized ? 280 : 360,
              maxHeight: 'calc(100vh - 180px)',
              borderRadius: 2,
            }),
            zIndex: 1200,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            transition: 'width 0.3s ease',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <PregnantWomanIcon />
              <Typography variant="subtitle1" fontWeight="bold">
                OB Measurements
              </Typography>
            </Box>
            <Box>
              <Tooltip title={isMinimized ? 'Expand' : 'Minimize'}>
                <IconButton
                  size="small"
                  onClick={() => setIsMinimized(!isMinimized)}
                  sx={{ color: 'white' }}
                >
                  {isMinimized ? <OpenInFullIcon fontSize="small" /> : <MinimizeIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton
                  size="small"
                  onClick={() => setIsExpanded(false)}
                  sx={{ color: 'white' }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Patient info bar */}
          {patientName && (
            <Box sx={{ bgcolor: 'grey.100', px: 2, py: 0.5 }}>
              <Typography variant="caption" color="textSecondary">
                {patientName} {gestationalAgeByLMP && `• LMP GA: ${gestationalAgeByLMP}`}
              </Typography>
            </Box>
          )}

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider', minHeight: { xs: 44, md: 48 } }}
          >
            <Tab
              icon={<AssessmentIcon fontSize="small" />}
              iconPosition="start"
              label={(isMinimized || isMobile) ? '' : 'Biometry'}
              sx={{ minHeight: { xs: 44, md: 48 }, fontSize: '0.8rem', minWidth: 0, px: { xs: 1, md: 2 } }}
            />
            <Tab
              icon={<FavoriteIcon fontSize="small" />}
              iconPosition="start"
              label={(isMinimized || isMobile) ? '' : 'Doppler'}
              sx={{ minHeight: { xs: 44, md: 48 }, fontSize: '0.8rem', minWidth: 0, px: { xs: 1, md: 2 } }}
            />
            <Tab
              icon={<TimelineIcon fontSize="small" />}
              iconPosition="start"
              label={(isMinimized || isMobile) ? '' : 'Chart'}
              sx={{ minHeight: { xs: 44, md: 48 }, fontSize: '0.8rem', minWidth: 0, px: { xs: 1, md: 2 } }}
            />
          </Tabs>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            <TabPanel value={activeTab} index={0}>
              <OBMeasurementPanel
                onMeasurementsChange={handleBiometryChange}
                lmpDate={lmpDate}
                examDate={examDate}
              />
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <DopplerAssessment
                onDopplerChange={handleDopplerChange}
                gaWeeks={biometryData?.results?.ga}
              />
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
              {biometryData?.results?.ga && biometryData?.results?.efw ? (
                <FetalGrowthChart
                  gaWeeks={biometryData.results.ga}
                  efwGrams={biometryData.results.efw}
                  percentile={biometryData.results.percentile?.percentile}
                  width={isMobile ? Math.min(window.innerWidth - 48, 400) : (isMinimized ? 240 : 320)}
                  height={isMobile ? 180 : 220}
                />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 4,
                    color: 'text.secondary',
                  }}
                >
                  <TimelineIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                  <Typography variant="body2" textAlign="center">
                    Enter biometry measurements to see growth chart
                  </Typography>
                </Box>
              )}
            </TabPanel>
          </Box>

          {/* Footer with actions */}
          <Divider />
          <Box sx={{ p: 1.5, bgcolor: 'grey.50', display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyToReport}
              disabled={!hasBiometry && !hasDoppler}
              fullWidth
              sx={{ textTransform: 'none' }}
            >
              {isMinimized ? 'Add' : 'Add to Report'}
            </Button>
            <Tooltip title="Copy to clipboard">
              <IconButton
                size="small"
                onClick={handleCopyToClipboard}
                disabled={!hasBiometry && !hasDoppler}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>
      </Slide>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default OBMeasurementOverlay;
